import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Close from "@mui/icons-material/Close";
import { CONTACT_LIMITS, newInquiryId, sendInquiry, validateInquiry } from "../utils/contactInquiry";
import "../styles/contact-dialog.css";

const emptyForm = () => ({ name: "", email: "", message: "", website: "" });

export default function ContactDialog({ open, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [copyState, setCopyState] = useState("");
  const [retryLocked, setRetryLocked] = useState(false);
  const requestId = useRef(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const feedback = useRef(null);
  const manualCopy = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const focusFeedback = () => {
    if (error?.field) document.getElementById(`contact-${error.field}`)?.focus();
    else if (receipt || error) feedback.current?.focus();
    else document.getElementById("contact-name")?.focus();
  };
  useEffect(() => {
    if (!open) return;
    if (error?.field) document.getElementById(`contact-${error.field}`)?.focus();
    else if (receipt || error) feedback.current?.focus();
    else document.getElementById("contact-name")?.focus();
  }, [open, error, receipt]);
  useEffect(() => { if (copyState === "manual") { manualCopy.current?.focus(); manualCopy.current?.select(); } }, [copyState]);
  function close() {
    if (inFlight.current) return;
    onClose();
  }
  function update(field, value) {
    if (inFlight.current || retryLocked) return;
    setForm(current => ({ ...current, [field]: value }));
    requestId.current = null;
    setError(null);
    setCopyState("");
  }
  const messageText = [`Name: ${form.name.trim()}`, `Reply email: ${form.email.trim()}`, "", form.message.trim(), ...(error?.reference ? ["", `Reference: ${error.reference}`] : [])].join("\n");
  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(messageText);
      if (mounted.current) setCopyState("copied");
    } catch { if (mounted.current) setCopyState("manual"); }
  }
  async function submit(event) {
    event.preventDefault();
    if (inFlight.current || receipt || error?.retryable === false) return;
    const validation = validateInquiry(form);
    if (validation.error) { setError(validation.error); return; }
    try { requestId.current ||= newInquiryId(); }
    catch (failure) { setError({ message: failure.message }); return; }
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    setCopyState("");
    try {
      const result = await sendInquiry({ request_id: requestId.current, ...validation.fields, website: form.website });
      if (mounted.current) { setReceipt(result.inquiry_id); setForm(emptyForm()); setRetryLocked(false); }
    } catch (failure) {
      if (mounted.current) {
        // An uncertain response may follow provider acceptance. Keep the same
        // payload and key until confirmed instead of silently sending a new one.
        setRetryLocked(!failure.field);
        if (failure.field) requestId.current = null;
        setError({ message: failure.message || "We could not send your message. Please try again.", field: failure.field, reference: failure.reference, retryable: failure.retryable });
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  }
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={{ className: "contact-dialog" }} aria-labelledby="contact-title" aria-describedby={!receipt ? "contact-description" : undefined} TransitionProps={{ onEntered: focusFeedback }}>
      <DialogTitle id="contact-heading" className="contact-title">
        <span id="contact-title">Get in touch</span>
        <IconButton className="contact-close" aria-label="Close contact form" onClick={close} disabled={submitting}><Close /></IconButton>
      </DialogTitle>
      <DialogContent className="contact-content">
        {receipt ? (
          <div className="contact-receipt" role="status" ref={feedback} tabIndex={-1}>
            <span className="contact-receipt-mark" aria-hidden="true">✓</span>
            <h3>Message received.</h3>
            <p>Thank you for reaching out. Your message has been submitted to our team for review.</p>
            <p className="contact-note">Reference: <span className="contact-reference">{receipt}</span></p>
            <button type="button" className="brand-button" onClick={close}>Done</button>
            <button type="button" className="contact-text-button" onClick={() => { setReceipt(null); requestId.current = null; }}>Send another message</button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={submit} noValidate aria-busy={submitting}>
            <p id="contact-description">Tell us what you’d like to build, automate, or discuss. Our team will follow up by email.</p>
            {[["name", "Your name"], ["email", "Email address"], ["message", "What do you need?"]].map(([field, label]) => {
              const attributes = { id: `contact-${field}`, name: field, value: form[field], required: true, disabled: submitting, readOnly: retryLocked, maxLength: CONTACT_LIMITS[field], onChange: event => update(field, event.target.value), "aria-invalid": error?.field === field || undefined, "aria-describedby": error?.field === field ? "contact-error" : undefined };
              return <div key={field} className={field === "message" ? "contact-field-full" : undefined}>
                <label htmlFor={attributes.id}>{label}</label>
                {field === "message" ? <textarea {...attributes} rows={5} /> : <input {...attributes} type={field === "email" ? "email" : "text"} autoComplete={field} autoCapitalize={field === "email" ? "none" : "words"} />}
              </div>;
            })}
            <div className="contact-honeypot" aria-hidden="true"><label htmlFor="contact-website">Leave this field empty</label><input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={event => update("website", event.target.value)} /></div>
            {error && <p className="contact-error" id="contact-error" role="alert" ref={feedback} tabIndex={-1}>{error.message}</p>}
            {error && !error.field && <div className="contact-fallback">
              {retryLocked && <p>Your submitted answers are kept unchanged so you can retry safely.</p>}
              <p>You can also copy your message and email it to <a href="mailto:team@grupa.ai">team@grupa.ai</a>.</p>
              <button type="button" className="contact-text-button" onClick={copyMessage}>Copy message</button>
              {copyState === "copied" && <p role="status">Copied. Paste it into an email to team@grupa.ai.</p>}
              {copyState === "manual" && <><p role="status">Select and copy your message below.</p><textarea aria-label="Message to copy" ref={manualCopy} readOnly value={messageText} /></>}
            </div>}
            <button type="submit" className="brand-button contact-submit" disabled={submitting || error?.retryable === false}>{submitting ? "Sending message…" : "Send message"}</button>
            <p className="contact-note" role="status">{submitting ? "Please keep this window open while we confirm receipt." : <>We’ll use these details to respond to your inquiry. <a href="/privacy">Privacy policy</a></>}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
